import React from 'react';

import {assert} from 'Asserts';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {ExecutionPlan} from 'studies/progress/Types';
import {CreateStudiesResponse} from 'studies/StudyDAO';
import {viewRecentlyCreatedStudies} from 'studies/workspace/StudySearchHelpers';
import {BasePendingTask} from 'util/resume-persisted-tasks/PersistedTaskTypes';
import * as TaskRunner from 'util/TaskRunner';

import {CreationTrayCallbacks, showCreationTrayTask} from './CreationTrayTask';

export const STUDY_ERROR_HEADER = 'Creating study failed.';
export const STUDY_TASK_ERROR_HEADER = 'Failed to fetch study creation progress.';

// Keep in sync with benchling.studies.progress.task_status.study_persisted_task_status
export interface CreateStudyTaskRequestMetadataCreate {
  name?: string;
  folderId: string;
}

// Keep in sync with benchling.studies.progress.task_status.study_persisted_task_status
export interface CreateStudyTaskRequestMetadata {
  creates: Array<CreateStudyTaskRequestMetadataCreate>;
}

export type StudyCreatePendingTask = BasePendingTask<
  'STUDY',
  CreateStudyTaskRequestMetadata,
  CreateStudiesResponse
>;

/**
 * @package - exported for testing
 */
export function getDescriptor(creates: Array<CreateStudyTaskRequestMetadataCreate>): JSX.Element {
  return creates.length > 1 ? (
    <>{`${creates.length} studies`}</>
  ) : creates[0].name ? (
    <>
      study <strong>{creates[0].name}</strong>
    </>
  ) : (
    <>study</>
  );
}

/**
 * Shows a tray task to track the progress of an asynchronous study creation.
 *
 * @param persistedPendingTask Specify the persisted task status when resuming polling
 */
export function showStudyCreationTrayTask(
  taskId: string,
  statusUrl: string,
  taskMeta: TaskRunner.TaskMeta,
  creates: Array<CreateStudyTaskRequestMetadataCreate>,
  callbacks: CreationTrayCallbacks<CreateStudiesResponse>,
  timingMeta?: ExecutionPlan,
  persistedPendingTask?: StudyCreatePendingTask
): void {
  showCreationTrayTask<'STUDY', CreateStudyTaskRequestMetadata, CreateStudiesResponse>(
    taskId,
    statusUrl,
    taskMeta,
    {
      creation: getDescriptor(creates),
      creationErrorHeader: STUDY_ERROR_HEADER,
      taskStatusErrorHeader: STUDY_TASK_ERROR_HEADER,
    },
    callbacks,
    timingMeta,
    persistedPendingTask
  );
}

export const STUDY_UNKNOWN_ERROR_ACTION = {
  text: 'View studies',
  description: (
    <>
      Click <i>View studies</i> to manually verify if the study was created. If not, consider <i>retrying.</i>
    </>
  ),
  onClick: viewRecentlyCreatedStudies,
};

export function onOpenStudyAfterCreate(response: CreateStudiesResponse): void {
  assert(response.createdIds.length === 1, 'Expected one study to be created');
  FileBrowserActions.openFile(response.createdIds[0]);
}
